/**
 * Tank Builder JS — 0722
 * ---------------------------------------------------------------------------
 * Drives the layered "build your own tank" experience: swatch selection per
 * slot, live stock/quantity capping, layered transparent-PNG preview
 * compositing, conditional addon questions, optional non-canvas upsell
 * items, and a multi-line-item add to cart with a shared _build_id property
 * so items group visually in cart.
 *
 * Handles two families of pickable item, both driven by the same
 * .pb-slot / .pb-current-selection / .pb-select-modal markup:
 *   - Required, canvas-linked slots (regulator, tank): always pre-selected
 *     on load, render into the stacked transparent-PNG preview.
 *   - Optional upsell items (thread protector, fill nipple cover, etc.):
 *     start unselected, never touch the canvas, marked in markup via
 *     data-optional="true" on the .pb-current-selection element. No other
 *     JS distinction is needed -- state.slots, updateSummary, and
 *     addToCart already treat an unset/null slot as "not included",
 *     which is exactly what an unselected upsell needs.
 *
 * Works against the markup emitted by sections/tank-builder-0722.liquid.
 * Multiple builder instances can exist on a page; each is namespaced by
 * its section id (passed into TankBuilder.init).
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var instances = {};

  function qs(root, sel) {
    return root.querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function Builder(rootId) {
    this.root = document.getElementById(rootId);
    if (!this.root) return;
    this.sectionId = this.root.dataset.sectionId;
    this.state = {
      slots: {},      // slotKey -> { variantId, price, available, inventoryQty, inventoryPolicy, title } (required slots and optional upsells both live here)
      addons: {}      // addonKey -> 'yes' | 'no'
    };
    this.slotEls = qsa(this.root, '.pb-slot');
    this.addonEls = qsa(this.root, '.pb-addon');
    this.bindEvents();
    this.initializeDefaults();
    this.updateAddonVisibility();
    this.updateSummary();
  }

  Builder.prototype.bindEvents = function () {
    var self = this;

    qsa(this.root, '.pb-row').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        self.selectSwatch(btn);
      });
    });

    qsa(this.root, '.pb-addon-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.selectAddon(btn);
      });
    });

    qsa(this.root, '.pb-current-selection').forEach(function (card) {
      card.addEventListener('click', function () {
        var slotEl = card.closest('.pb-slot');
        if (slotEl.dataset.slotSoldOut === 'true') return;
        self.openModal(slotEl.dataset.slotKey);
      });
    });

    qsa(this.root, '.pb-select-modal-close').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.closeModal(btn.dataset.slotKey);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.activeModalSlotKey) {
        self.closeModal(self.activeModalSlotKey);
      }
    });

    var addBtn = qs(this.root, '.pb-add-to-cart');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        self.addToCart();
      });
    }
  };

  /**
   * On page load, Liquid pre-selects a default variant per REQUIRED slot
   * (first in-stock item in that slot's collection) by rendering it with
   * .pb-row-selected in the (hidden) select modal. Optional upsell items
   * deliberately have no .pb-row-selected row, so this loop naturally
   * leaves them unset -- no special-casing needed here.
   *
   * Addon questions (e.g. Installation) can also start pre-answered, via
   * each block's "Pre-selected answer" setting -- Liquid already renders
   * the matching button with .pb-addon-selected so it's visually correct
   * with no flash on load; this just makes sure the underlying state
   * (self.state.addons) agrees, so updateSummary() correctly treats it
   * as already answered instead of leaving Add to Cart waiting on a
   * click that visually already happened. updateAddonVisibility() (run
   * right after this in the constructor) will still correctly clear this
   * back out if this addon's required slots aren't actually filled --
   * same as it always has, unaffected by this.
   */
  Builder.prototype.initializeDefaults = function () {
    var self = this;
    qsa(this.root, '.pb-slot').forEach(function (slotEl) {
      var slotKey = slotEl.dataset.slotKey;

      if (slotEl.dataset.slotSoldOut === 'true') {
        self.state.slots[slotKey] = null;
        return;
      }

      var defaultBtn = slotEl.querySelector('.pb-row.pb-row-selected');
      if (defaultBtn) {
        self.applySwatchSelection(slotKey, defaultBtn);
      }
    });

    qsa(this.root, '.pb-addon').forEach(function (addonEl) {
      var defaultValue = addonEl.dataset.defaultValue;
      if (defaultValue !== 'yes' && defaultValue !== 'no') return;
      self.state.addons[addonEl.dataset.addonKey] = defaultValue;
    });
  };

  Builder.prototype.openModal = function (slotKey) {
    var modal = document.getElementById('pb-modal-' + this.sectionId + '-' + slotKey);
    if (!modal) return;
    modal.hidden = false;
    document.body.classList.add('pb-modal-open');
    this.activeModalSlotKey = slotKey;
  };

  Builder.prototype.closeModal = function (slotKey) {
    var modal = document.getElementById('pb-modal-' + this.sectionId + '-' + slotKey);
    if (!modal) return;
    modal.hidden = true;
    document.body.classList.remove('pb-modal-open');
    this.activeModalSlotKey = null;
  };

  /**
   * Swaps the current-selection thumbnail element between its "no image
   * yet" placeholder (a plain div, matching required-slot markup for
   * products with no image at all) and a real <img>. A plain element
   * can't change tagName in place, so this replaces the node and keeps
   * the same id, which is what every id-based lookup elsewhere depends on.
   */
  function swapThumbToImage(thumbEl, src) {
    if (!thumbEl) return thumbEl;
    if (thumbEl.tagName === 'IMG') {
      thumbEl.src = src;
      thumbEl.hidden = false;
      return thumbEl;
    }
    var img = document.createElement('img');
    img.className = 'pb-current-thumb';
    img.id = thumbEl.id;
    img.src = src;
    img.alt = '';
    thumbEl.replaceWith(img);
    return img;
  }
  function swapThumbToEmpty(thumbEl, cardEl) {
    if (!thumbEl) return thumbEl;
    if (thumbEl.tagName !== 'IMG') return thumbEl;
    var div = document.createElement('div');
    div.className = 'pb-current-thumb pb-current-thumb-empty';
    div.id = thumbEl.id;
    var previewUrl = cardEl && cardEl.dataset.previewImage;
    if (previewUrl) {
      div.classList.add('pb-current-thumb-preview');
      var photo = document.createElement('img');
      photo.className = 'pb-thumb-preview-photo';
      photo.src = previewUrl;
      photo.alt = '';
      div.appendChild(photo);
      var wash = document.createElement('span');
      wash.className = 'pb-thumb-preview-wash';
      wash.setAttribute('aria-hidden', 'true');
      div.appendChild(wash);
      var badge = document.createElement('span');
      badge.className = 'pb-thumb-add-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = '+';
      div.appendChild(badge);
    }
    thumbEl.replaceWith(div);
    return div;
  }

  /**
   * Updates the collapsed "current selection" card (thumbnail, title,
   * price, and -- for optional upsell cards only -- the Add/Select button
   * text and the empty-state border) to reflect whatever was just chosen,
   * or the "not selected" state.
   *
   * isOptional (read from data-optional="true" on the card, set only by
   * upsell blocks in the Liquid) gates every piece of new-in-0722
   * behavior. Required slots never carry that attribute, so this function
   * behaves exactly as it did before for them.
   */
  Builder.prototype.updateCurrentSelectionCard = function (slotKey, btn) {
    var cardEl = document.getElementById('pb-current-' + this.sectionId + '-' + slotKey);
    var thumbEl = document.getElementById('pb-current-thumb-' + this.sectionId + '-' + slotKey);
    var titleEl = document.getElementById('pb-current-title-' + this.sectionId + '-' + slotKey);
    var priceEl = document.getElementById('pb-current-price-' + this.sectionId + '-' + slotKey);
    if (!titleEl || !priceEl) return;

    var variantId = btn.dataset.variantId || null;
    var isOptional = cardEl && cardEl.dataset.optional === 'true';

    if (!variantId) {
      titleEl.textContent = btn.querySelector('.pb-row-title') ? btn.querySelector('.pb-row-title').textContent : 'None';
      priceEl.textContent = '';
      if (isOptional) {
        thumbEl = swapThumbToEmpty(thumbEl, cardEl);
      } else if (thumbEl && thumbEl.tagName === 'IMG') {
        thumbEl.src = '';
        thumbEl.hidden = true;
      }
      return;
    }

    titleEl.textContent = btn.dataset.variantTitle === 'Default Title'
      ? btn.dataset.productTitle
      : btn.dataset.productTitle + ' - ' + btn.dataset.variantTitle;
    priceEl.textContent = formatMoney(parseInt(btn.dataset.price, 10));
    if (btn.dataset.swatchImage) {
      thumbEl = swapThumbToImage(thumbEl, btn.dataset.swatchImage);
    }
  };

  Builder.prototype.applySwatchSelection = function (slotKey, btn) {
    var variantId = btn.dataset.variantId || null;

    if (!variantId) {
      this.state.slots[slotKey] = null;
      this.updateLayer(slotKey, null);
    } else {
      var qtyAvailable = btn.dataset.inventoryPolicy === 'continue'
        ? Infinity
        : parseInt(btn.dataset.inventoryQty, 10);

      this.state.slots[slotKey] = {
        variantId: variantId,
        price: parseInt(btn.dataset.price, 10),
        available: btn.dataset.available === 'true',
        inventoryQty: qtyAvailable,
        qty: 1,
        title: btn.dataset.variantTitle === 'Default Title'
          ? btn.dataset.productTitle
          : btn.dataset.productTitle + ' - ' + btn.dataset.variantTitle,
        builderImage: btn.dataset.builderImage
      };

      this.updateLayer(slotKey, btn.dataset.builderImage);
    }

    this.updateCurrentSelectionCard(slotKey, btn);
  };

  Builder.prototype.selectSwatch = function (btn) {
    var slotKey = btn.dataset.slotKey;

    var grid = document.getElementById('pb-grid-' + this.sectionId + '-' + slotKey);
    qsa(grid, '.pb-row').forEach(function (b) {
      b.classList.remove('pb-row-selected');
    });
    btn.classList.add('pb-row-selected');

    this.applySwatchSelection(slotKey, btn);
    this.closeModal(slotKey);

    this.updateAddonVisibility();
    this.updateSummary();
  };

  /**
   * No-ops safely for upsell keys, since upsell blocks render no
   * .pb-layer image at all -- getElementById returns null and this
   * returns immediately. Nothing here needed to change for 0722.
   */
  Builder.prototype.updateLayer = function (slotKey, imageUrl) {
    var img = document.getElementById('pb-layer-' + this.sectionId + '-' + slotKey);
    if (!img) return;
    if (imageUrl) {
      img.src = imageUrl;
      img.hidden = false;
    } else {
      img.src = '';
      img.hidden = true;
    }
    this.updatePlaceholderVisibility();
  };

  Builder.prototype.updatePlaceholderVisibility = function () {
    var placeholder = document.getElementById('pb-placeholder-' + this.sectionId);
    if (!placeholder) return;
    var anyVisible = qsa(this.root, '.pb-layer').some(function (img) {
      return !img.hidden;
    });
    placeholder.hidden = anyVisible;
  };

  Builder.prototype.updateAddonVisibility = function () {
    var self = this;
    this.addonEls.forEach(function (addonEl) {
      var requires = (addonEl.dataset.requiresSlots || '')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);
      var allFilled = requires.every(function (key) {
        return !!self.state.slots[key];
      });
      addonEl.hidden = !allFilled;
      if (!allFilled) {
        var addonKey = addonEl.dataset.addonKey;
        delete self.state.addons[addonKey];
        qsa(addonEl, '.pb-addon-btn').forEach(function (b) {
          b.classList.remove('pb-addon-selected');
        });
      }
    });
  };

  Builder.prototype.selectAddon = function (btn) {
    var addonKey = btn.dataset.addonKey;
    var value = btn.dataset.value;
    this.state.addons[addonKey] = value;

    var addonEl = document.getElementById('pb-addon-' + this.sectionId + '-' + addonKey);
    qsa(addonEl, '.pb-addon-btn').forEach(function (b) {
      b.classList.remove('pb-addon-selected');
    });
    btn.classList.add('pb-addon-selected');

    this.updateSummary();
  };

  Builder.prototype.updateSummary = function () {
    var self = this;
    var total = 0;
    var anySelected = false;

    for (var key in this.state.slots) {
      var slot = this.state.slots[key];
      if (slot) {
        anySelected = true;
        var qty = slot.qty || 1;
        total += slot.price * qty;
      }
    }

    var subtotalEl = document.getElementById('pb-subtotal-' + this.sectionId);
    if (subtotalEl) subtotalEl.textContent = formatMoney(total);

    var allVisibleAddonsAnswered = this.addonEls.every(function (addonEl) {
      if (addonEl.hidden) return true;
      var addonKey = addonEl.dataset.addonKey;
      return !!self.state.addons[addonKey];
    });

    var addBtn = qs(this.root, '.pb-add-to-cart');
    if (addBtn) addBtn.disabled = !anySelected || !allVisibleAddonsAnswered;
  };

  Builder.prototype.showError = function (msg) {
    var el = document.getElementById('pb-error-' + this.sectionId);
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, 5000);
  };

  Builder.prototype.getCartSectionIdsToRender = function () {
    var ids = [];
    var cartItemsEl = document.getElementById('cart-items');
    if (cartItemsEl && cartItemsEl.dataset.section) {
      ids.push(cartItemsEl.dataset.section);
    } else {
      ids.push('cart-drawer');
    }
    ids.push('cart-icon-bubble');
    return ids;
  };

  Builder.prototype.renderCartDrawerAndOpen = function (parsedState) {
    var self = this;
    var renderedSomething = false;

    if (parsedState && parsedState.sections && parsedState.sections['cart-drawer']) {
      try {
        var html = parsedState.sections['cart-drawer'];
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var sourceCartItems = parsed.getElementById('cart-items');
        var targetCartItems = document.getElementById('cart-items');

        if (sourceCartItems && targetCartItems) {
          targetCartItems.innerHTML = sourceCartItems.innerHTML;
          targetCartItems.dataset.empty = sourceCartItems.dataset.empty || 'false';
          renderedSomething = true;
        }

        var sourceBubble = parsed.getElementById('cart-icon-bubble');
        var targetBubble = document.getElementById('cart-icon-bubble');
        if (sourceBubble && targetBubble) {
          targetBubble.innerHTML = sourceBubble.innerHTML;
        }

        var sourceSummary = parsed.querySelector('.cart-drawer__summary');
        var targetSummary = document.querySelector('.cart-drawer__summary');
        if (sourceSummary && targetSummary) {
          targetSummary.innerHTML = sourceSummary.innerHTML;
        }

        var targetShipping = document.querySelector('.free-shipping-container');
        var sourceShipping = parsed.querySelector('.free-shipping-container');
        if (!targetShipping) {
          var drawerContent = document.querySelector('.drawer__content');
          if (sourceShipping && drawerContent) {
            drawerContent.insertAdjacentHTML('afterbegin', sourceShipping.outerHTML);
          }
        } else {
          if (sourceShipping) {
            targetShipping.innerHTML = sourceShipping.innerHTML;
          }
        }
      } catch (e) {
        console.error('[TankBuilder] Manual cart-items update failed:', e);
      }
    }

    self.openCartDrawer();

    if (!renderedSomething) {
      // #cart-items wasn't found in either this page or the returned
      // section HTML -- inspect the actual ids in parsedState.sections
      // and report back.
    }
  };

  Builder.prototype.openCartDrawer = function () {
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('cart:show'));

    var drawerSelectors = [
      'cart-drawer',
      '#CartDrawer',
      '[data-cart-drawer]',
      '.cart-drawer',
      '#cart-drawer',
      '#sidebar-cart',
      '#mini-cart'
    ];
    var drawer = null;
    for (var i = 0; i < drawerSelectors.length; i++) {
      var el = document.querySelector(drawerSelectors[i]);
      if (el) { drawer = el; break; }
    }

    if (drawer) {
      if (typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      if (typeof drawer.show === 'function') {
        drawer.show();
        return;
      }
      drawer.classList.add('open', 'active', 'is-open', 'drawer--open');
      drawer.setAttribute('open', '');
      drawer.removeAttribute('hidden');
      return;
    }

    var triggerSelectors = [
      '[data-cart-icon]',
      '#cart-icon-bubble',
      '.header__icon--cart',
      '[data-drawer-trigger="cart"]',
      'button[aria-controls*="cart" i]',
      'a[href="/cart"][data-action]'
    ];
    for (var j = 0; j < triggerSelectors.length; j++) {
      var trigger = document.querySelector(triggerSelectors[j]);
      if (trigger) {
        trigger.click();
        return;
      }
    }

    window.location.href = '/cart';
  };

  Builder.prototype.showSuccess = function () {
    var el = document.getElementById('pb-success-' + this.sectionId);
    if (!el) return;
    el.hidden = false;
    setTimeout(function () { el.hidden = true; }, 4000);
  };

  Builder.prototype.verifyLiveStock = function (variantId, requestedQty) {
    return fetch('/products/' + variantId + '.js')
      .catch(function () { return null; });
  };

  Builder.prototype.addToCart = function () {
    var self = this;

    var unansweredRequired = this.addonEls.find(function (addonEl) {
      if (addonEl.hidden) return false;
      var addonKey = addonEl.dataset.addonKey;
      return !self.state.addons[addonKey];
    });
    if (unansweredRequired) {
      var labelText = unansweredRequired.dataset.questionLabel || 'this question';
      this.showError('Please answer "' + labelText + '" before adding to cart.');
      unansweredRequired.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    var items = [];
    var buildId = 'build_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);

    var filledSlots = [];
    for (var key in this.state.slots) {
      if (this.state.slots[key]) filledSlots.push(key);
    }

    var addonNote = null;
    for (var addonKey in this.state.addons) {
      addonNote = addonKey + ': ' + (this.state.addons[addonKey] === 'yes' ? 'Requested' : 'Not Requested');
    }

    filledSlots.forEach(function (slotKey, idx) {
      var slot = self.state.slots[slotKey];
      var qty = slot.qty || 1;

      if (qty > slot.inventoryQty) {
        self.showError('Not enough stock for ' + slot.title + '.');
        items = null;
        return;
      }

      var properties = { '_build_id': buildId, '_build_slot': slotKey };
      if (addonNote && idx === 0) {
        properties['Installation'] = self.state.addons[Object.keys(self.state.addons)[0]] === 'yes' ? 'Requested' : 'Not Requested';
      }

      items.push({
        id: slot.variantId,
        quantity: qty,
        properties: properties
      });
    });

    if (!items || items.length === 0) {
      if (items === null) return;
      this.showError('Please select at least one option before adding to cart.');
      return;
    }

    var addBtn = qs(this.root, '.pb-add-to-cart');
    addBtn.disabled = true;
    addBtn.textContent = 'Adding...';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: self.getCartSectionIdsToRender(),
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw new Error(err.description || 'Could not add to cart.');
          });
        }
        return res.json();
      })
      .then(function (parsedState) {
        self.showSuccess();
        addBtn.textContent = 'Add To Cart';
        addBtn.disabled = false;
        self.renderCartDrawerAndOpen(parsedState);
      })
      .catch(function (err) {
        self.showError(err.message || 'Something went wrong adding to cart.');
        addBtn.textContent = 'Add To Cart';
        addBtn.disabled = false;
      });
  };

  /**
   * Drives the mobile-only "full size normally, shrinks to a cropped top
   * -half and sticks once the customer scrolls down to make choices"
   * canvas behavior. Independent of Builder's selection/cart state --
   * pure scroll-driven presentation, so it's a separate function rather
   * than a Builder method.
   *
   * This is a CONTINUOUS scroll-linked animation, not a discrete on/off
   * toggle. An earlier version used an IntersectionObserver to flip a
   * single stuck/unstuck state at the exact moment .pb-preview-col
   * caught -- but that meant nothing happened during the scroll leading
   * up to that moment, so the top of the artwork could already be at
   * risk before the crop+zoom had done anything to compensate. Instead,
   * both the height (crop) and the zoom now ramp in together as a
   * function of scroll position, over TRANSITION_DISTANCE px BEFORE the
   * catch point, finishing right as .pb-preview-col actually sticks --
   * not starting fresh at that point.
   *
   * .pb-preview-sentinel (in its own dedicated grid row, independent of
   * .pb-preview-col's height -- see the CSS) provides a stable reference
   * position: its distance from the top of the viewport is what scroll
   * progress is measured against on every scroll tick, throttled to one
   * calculation per animation frame.
   *
   * Explicit pixel height and an inline transform are set directly, with
   * NO CSS transition on either -- for a scroll-linked effect the value
   * needs to track the current scroll position exactly, on every frame;
   * a CSS transition would make it lag behind and chase, rather than
   * following the finger 1:1.
   */
  function initStickyPreviewCrop(root) {
    var previewCol = root.querySelector('.pb-preview-col');
    var canvas = root.querySelector('.pb-canvas');
    var sentinel = root.querySelector('.pb-preview-sentinel');
    if (!previewCol || !canvas || !sentinel) return;

    // How much of the full square stays visible once fully cropped, as a
    // fraction of the canvas width (== the canvas's own full height,
    // since it's a 1:1 square). Tune directly by eye.
    var STUCK_HEIGHT_RATIO = 0.62;
    // How much the image zooms in at full progress. Tune directly by eye.
    var ZOOM_SCALE = 1.50;
    // Scroll distance, in px, over which the crop+zoom ramps from fully
    // off to fully on. Smaller = snappier/quicker to engage, larger =
    // more gradual. Tune directly by eye.
    var TRANSITION_DISTANCE = 120;

    var mobileQuery = window.matchMedia('(max-width: 860px)');
    var fullWidth = 0;
    var ticking = false;

    function measure() {
      fullWidth = previewCol.getBoundingClientRect().width;
    }

    function applyProgress(progress) {
      if (!mobileQuery.matches || !fullWidth) {
        previewCol.style.height = '';
        canvas.style.transform = '';
        previewCol.classList.remove('pb-preview-col--stuck');
        return;
      }
      var height = fullWidth - (fullWidth * (1 - STUCK_HEIGHT_RATIO) * progress);
      var scale = 1 + (ZOOM_SCALE - 1) * progress;
      previewCol.style.height = height + 'px';
      canvas.style.transform = 'scale(' + scale + ')';
      previewCol.classList.toggle('pb-preview-col--stuck', progress >= 0.98);
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (!mobileQuery.matches) {
          applyProgress(0);
          return;
        }
        var sentinelTop = sentinel.getBoundingClientRect().top;
        var progress = (TRANSITION_DISTANCE - sentinelTop) / TRANSITION_DISTANCE;
        progress = Math.min(1, Math.max(0, progress));
        applyProgress(progress);
      });
    }

    measure();
    applyProgress(0);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      measure();
      onScroll();
    });
  }

  window.TankBuilder = {
    init: function (rootId) {
      if (instances[rootId]) return instances[rootId];
      var instance = new Builder(rootId);
      instances[rootId] = instance;
      initStickyPreviewCrop(instance.root);
      return instance;
    }
  };
})();